/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1777939200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue May 05 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "apache-kafka",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to your Kafka server",
          "depth": 1,
          "id": "connect-to-your-kafka-server"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Configure your events trigger",
          "depth": 1,
          "id": "configure-your-events-trigger"
        }, {
          "value": "Choose your topic",
          "depth": 1,
          "id": "choose-your-topic"
        }, {
          "value": "Customize your message",
          "depth": 1,
          "id": "customize-your-message"
        }, {
          "value": "Configure optional message properties",
          "depth": 1,
          "id": "configure-optional-message-properties"
        }, {
          "value": "Configure initial sync behavior",
          "depth": 1,
          "id": "configure-initial-sync-behavior"
        }, {
          "value": "Validate your setup",
          "depth": 0,
          "id": "validate-your-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      a: "a",
      strong: "strong",
      code: "code",
      img: "img",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span",
      h5: "h5",
      ol: "ol",
      hr: "hr"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch integrates directly with Apache Kafka to support high-throughput, distributed, or asynchronous workloads, letting you build a custom connector to your internal systems."
      }), "\n", _jsx(_components.p, {
        children: "This destination was designed to be as flexible as possible. Some of its capabilities include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "connecting to multiple brokers"
        }), "\n", _jsx(_components.li, {
          children: "authenticating with Simple Authentication and Security Layer (SASL)"
        }), "\n", _jsx(_components.li, {
          children: "using your own certificate authority"
        }), "\n", _jsx(_components.li, {
          children: "publishing different topics for each message trigger"
        }), "\n", _jsx(_components.li, {
          children: "defining custom ordering and partition keys"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports all managed Kafka services (Amazon MSK, Confluent Cloud, etc.) and can also connect to self-hosted instances."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-kafka-server",
        children: "Connect to your Kafka server"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to connect to your Kafka server, you need to enter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "a client ID"
        }), "\n", _jsx(_components.li, {
          children: "your broker information"
        }), "\n", _jsx(_components.li, {
          children: "authentication method used in your Kafka server configuration"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "client-id",
        children: "Client ID"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://kafka.apache.org/documentation/#design_quotasgroups",
          children: _jsx(_components.strong, {
            children: "Client ID"
          })
        }), " is a logical identifier of a client application—in this case it's the Hightouch Apache Kafka destination. It's used to distinguish each running application of your Kafka server. You can choose to name this anything you want that fits your use case."]
      }), "\n", _jsx(_components.h4, {
        id: "brokers",
        children: "Brokers"
      }), "\n", _jsxs(_components.p, {
        children: ["For Hightouch to sync data to the right Kafka Brokers, you need to provide the ", _jsx(_components.strong, {
          children: "host"
        }), " and ", _jsx(_components.strong, {
          children: "port"
        }), " number in the format ", _jsx(_components.code, {
          children: "{host}:{port}"
        }), ". If you want to configure the destination to connect to multiple brokers, you can input the details separated by a comma. For example, you could enter ", _jsx(_components.code, {
          children: "{host1}:{port1},{host2}:{port2}"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "authentication",
        children: "Authentication"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect to your Kafka server either with SASL or directly without authentication. For security purposes, it's best to configure your Kafka server to require SASL authentication when syncing production data, and to only omit authentication requirements for testing purposes."
      }), "\n", _jsx(_components.p, {
        children: "When configuring your SASL mechanism in Hightouch, you have four options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "PLAIN"
        }), "\n", _jsx(_components.li, {
          children: "SCRAM SHA256"
        }), "\n", _jsx(_components.li, {
          children: "SCRAM SHA512"
        }), "\n", _jsx(_components.li, {
          children: "AWS IAM"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All four provide the option to include your own self-signed certificate authority."
      }), "\n", _jsx(_components.h4, {
        id: "plainscram",
        children: "PLAIN/SCRAM"
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Username"
        }), " and ", _jsx(_components.strong, {
          children: "Password"
        }), ", enter your username and password configured in your Kafka server. If you are using a managed Kafka service, your details can usually be found in your environment's settings or as an API key and secret."]
      }), "\n", _jsx(_components.h4, {
        id: "aws-iam",
        children: "AWS IAM"
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to authenticate via AWS IAM, we assume your Kafka server is configured to use AWS IAM as an authentication method, that is ", _jsx(_components.a, {
          href: "https://github.com/STACK-Fintech/kafka-auth-aws-iam",
          children: "STACK's Kafka AWS IAM LoginModule"
        }), " or a compatible alternative is installed on all target brokers."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Authorization Identity"
          }), " must be the ", _jsx(_components.code, {
            children: "aws:userid"
          }), " of the AWS IAM identity. Typically, you can retrieve this value using the ", _jsx(_components.code, {
            children: "aws iam get-user"
          }), " or ", _jsx(_components.code, {
            children: "aws iam get-role"
          }), " commands of the AWS CLI toolkit. The ", _jsx(_components.code, {
            children: "aws:userid"
          }), " is usually listed as the ", _jsx(_components.code, {
            children: "UserId"
          }), " or ", _jsx(_components.code, {
            children: "RoleId"
          }), " property of the response."]
        }), "\n", _jsxs(_components.li, {
          children: ["You can find your ", _jsx(_components.strong, {
            children: "Access Key ID"
          }), ", ", _jsx(_components.strong, {
            children: "Secret Access Key"
          }), ", and ", _jsx(_components.strong, {
            children: "Session Token"
          }), " in your AWS account. For more information on AWS IAM credentials and authentication, refer to the official ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html",
            children: "AWS docs"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Once you've connected your Kafka server to Hightouch, you've completed setup for a Apache Kafka destination in your Hightouch workspace. The next step is to configure a sync that send messages whenever rows are added, changed, or removed in your model."
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-events-trigger",
        children: "Configure your events trigger"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch monitors your data model for added, changed, and removed rows. In this step, you specify which of these events should trigger message publishing."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-events-trigger.png",
          alt: "Events to trigger message publishing"
        })
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-topic",
        children: "Choose your topic"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you choose which topics to publish the messages to. Hightouch allows you to sync to existing topics that are already in your Kafka cluster."
      }), "\n", _jsxs(_components.p, {
        children: ["Suppose you want to sync to multiple existing topics but don't want to create a new sync for every topic. As long as your model has a column associated to topic names in your Kafka cluster, Hightouch can sync to multiple Apache Kafka topics in just one sync.\nTo enable this feature, toggle ", _jsx(_components.strong, {
          children: "USE COLUMN"
        }), ", and select a column in your model containing the topic name rows."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-multiple-topics.png",
          alt: "Toggle use column to send message to multiple topics"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When syncing to multiple topics, if a topic name in the selected column of\nyour model doesn't exist in the Kafka cluster, then the entire batch of\nmessages will fail to sync."
        })
      }), "\n", _jsx(_components.h3, {
        id: "customize-your-message",
        children: "Customize your message"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-customize-message-data.png",
          alt: "JSON message options in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to build the JSON message data object using data from your model."
      }), "\n", _jsx(_components.p, {
        children: "This destination offers three methods of composing a JSON object:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-json-editor",
            children: "Using a JSON editor"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-one-column-from-model",
            children: "Using one column from a model"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#use-multiple-columns-from-model",
            children: "Using multiple columns from a model"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-json-editor",
        children: "Use JSON editor"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-json-editor.png",
          alt: "Selecting the JSON editor method in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["With the JSON editor, you can compose any JSON object using the ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid template language"
        }), ". This is useful for complex message data bodies containing nested objects and arrays, which can sometimes be difficult to model entirely in SQL."]
      }), "\n", _jsx(_components.p, {
        children: "Suppose your data model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "full_name"
            }), _jsx(_components.th, {
              children: "age"
            }), _jsx(_components.th, {
              children: "email_address"
            }), _jsx(_components.th, {
              children: "phone_number"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "John Doe"
            }), _jsx(_components.td, {
              children: "30"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "john@example.com"
              })
            }), _jsx(_components.td, {
              children: "+14158675309"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "And you want your message data like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"age\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "30"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"contact_info\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"john@example.com\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"phone\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"+14158675309\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Your Liquid template should look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-txt",
          children: "{\n  \"name\": \"{{row.full_name}}\",\n  \"age\": {{row.age}},\n  \"contact_info\": [\n    {\n      \"type\": \"email\",\n      \"value\": \"{{row.email_address}}\"\n    },\n    {\n      \"type\": \"phone\",\n      \"value\": \"{{row.phone_number}}\"\n    }\n  ]\n}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This makes it so you can reference any column using the syntax ", _jsx(_components.code, {
          children: "{{row.column_name}}"
        }), ". You can also use advanced Liquid features to incorporate control flow and loops into your dynamic message data."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "When injecting strings into your JSON object, be sure to surround the Liquid\ntag in double quotes."
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-one-column-from-model",
        children: "Use one column from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-one-column-from-model.png",
          alt: "Selecting using one column from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you're already storing JSON data in your source, or if you have the ability to construct a JSON object using SQL, you can select one column in your model that already contains the full message data."
      }), "\n", _jsx(_components.p, {
        children: "This setting is commonly used when syncing web events that have already been collected and stored as JSON objects in your database."
      }), "\n", _jsx(_components.h4, {
        id: "use-multiple-columns-from-model",
        children: "Use multiple columns from model"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-multiple-columns-from-model.png",
          alt: "Selecting using multiple columns from the model as the JSON construction method in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "For the simplest use cases, Hightouch can construct a JSON object with key/value pairs based on multiple columns in your model."
      }), "\n", _jsx(_components.p, {
        children: "Suppose your model looks like this:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "email"
            }), _jsx(_components.th, {
              children: "first_name"
            }), _jsx(_components.th, {
              children: "last_name"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alice.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bob.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "carol.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Carol"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The field mapping in the preceding screenshot would generate the following message data for the first row:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Alice\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alice.doe@example.com\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can use the field mapper to rename fields. For example, ", _jsx(_components.code, {
            children: "first_name"
          }), " can\nbe mapped to ", _jsx(_components.code, {
            children: "customer_first_name"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-optional-message-properties",
        children: "Configure optional message properties"
      }), "\n", _jsx(_components.p, {
        children: "Along with your row data in JSON format, you can optionally include ordering keys to configure the order your Kafka cluster receives message and metadata fields as headers."
      }), "\n", _jsx(_components.h5, {
        id: "partition",
        children: _jsx(_components.code, {
          children: "partition"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A number field that determines which partition to send the message to. This field takes precedence over the ", _jsx(_components.code, {
          children: "key"
        }), " field. That is if you provided ", _jsx(_components.code, {
          children: "partition"
        }), " and ", _jsx(_components.code, {
          children: "key"
        }), ", the message will be sent to the partition stated in the ", _jsx(_components.code, {
          children: "partition"
        }), " field and not the ", _jsx(_components.code, {
          children: "key"
        }), " field. Hightouch automatically tries to cast the value to a number. If we can't cast the value to a number then it is sent as ", _jsx(_components.code, {
          children: "null"
        }), "."]
      }), "\n", _jsx(_components.h5, {
        id: "key",
        children: _jsx(_components.code, {
          children: "key"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If no ", _jsx(_components.code, {
          children: "partition"
        }), " column is selected but a ", _jsx(_components.code, {
          children: "key"
        }), " of string type is selected, then Kafka chooses a partition to send the message to based on a murmur2 hash of the key. For example, if you use an ", _jsx(_components.code, {
          children: "orderId"
        }), " as the key, you can ensure that all messages regarding that order will be processed in order."]
      }), "\n", _jsxs(_components.p, {
        children: ["If no ", _jsx(_components.code, {
          children: "partition"
        }), " or ", _jsx(_components.code, {
          children: "key"
        }), " is included, then the message will be sent to a partition in a round-robin fashion."]
      }), "\n", _jsx(_components.h5, {
        id: "headers",
        children: _jsx(_components.code, {
          children: "headers"
        })
      }), "\n", _jsx(_components.p, {
        children: "This is an object containing key/value pairs of custom mapping fields."
      }), "\n", _jsx(_components.h3, {
        id: "configure-initial-sync-behavior",
        children: "Configure initial sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to handle rows present in your model results during the first sync run."
      }), "\n", _jsx(_components.p, {
        children: "Certain workflows may require performing a backfill of all rows during the initial sync. For other use cases, you might only want to send messages in response to future data changes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-apache-kafka-initial-sync-behaviour.png",
          alt: "Declaring how to handle initial sync behavior in Hightouch"
        })
      }), "\n", _jsx(_components.h2, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After you configure your sync, run a test to confirm data flows correctly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start with a small test audience or a single record."
        }), "\n", _jsxs(_components.li, {
          children: ["Trigger a manual sync from the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: "Syncs overview"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["After the sync completes, verify the messages were received by your Kafka consumer. You can use a CLI consumer like ", _jsx(_components.code, {
            children: "kafka-console-consumer"
          }), " or your existing consumer application to confirm messages appear on the expected topic with the correct payload structure."]
        }), "\n", _jsx(_components.li, {
          children: "If the sync shows errors, check the sync run log in Hightouch for specific error messages."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
